import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged, User } from 'firebase/auth';
import { TariffItem } from '../types/tariff';
import SearchBar from '../componenets/SearchBar';
import TariffTable from '../componenets/TariffTable';
import TariffDetailModal from '../componenets/TariffDetailModal';
import {
    searchTariffData,
    getCategories,
    getTaxDetails,
    SearchApiResponse,
    sendWakeUpRequest
} from '../api/tariffApi';
import useDebounce from '../hooks/useDebounce';
import '../App.css';
import Navbar from '../componenets/Navbar';
import { Analytics } from "@vercel/analytics/react";
import Footer from '../componenets/Footer';
import { FaLock, FaSearch, FaCalculator } from 'react-icons/fa';

const PREFERENTIAL_DUTY_COLS = [
    'AD', 'BN', 'GT', 'IN', 'PK', 'SA', 'SF', 'SD', 'SG', 'Preferential Duty AP'
];

const formatTariffItemForDisplay = (item: TariffItem): TariffItem => {
    const formattedItem: TariffItem = { ...item };
    for (const col of PREFERENTIAL_DUTY_COLS) {
        if (formattedItem.hasOwnProperty(col)) {
            const value = formattedItem[col];
            if (value !== null && value !== undefined) {
                if (
                    (typeof value === 'number' && value === 0) ||
                    (typeof value === 'string' && (value === '0' || value === '0%'))
                ) {
                    formattedItem[col] = 'Free';
                }
            }
        }
    }
    return formattedItem;
};

const Home: React.FC = () => {
    const navigate = useNavigate();
    
    const [currentUser, setCurrentUser] = useState<User | null>(null);

    const [searchCount, setSearchCount] = useState<number>(() => 
        Number(localStorage.getItem('search_usage') || 0)
    );
    const [calcCount, setCalcCount] = useState<number>(() => 
        Number(localStorage.getItem('calc_usage') || 0)
    );

    const SEARCH_LIMIT = 10;
    const CALC_LIMIT = 7;

    const isSearchLocked = !currentUser && searchCount >= SEARCH_LIMIT;
    const isCalcLocked = !currentUser && calcCount >= CALC_LIMIT;

    const [searchTerm, setSearchTerm] = useState<string>('');
    const [, setCategories] = useState<string[]>([]);
    const [selectedCategory] = useState<string | null>(null);
    const [filteredResults, setFilteredResults] = useState<TariffItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedTariffDetail, setSelectedTariffDetail] = useState<TariffItem | null>(null);

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const [displayedQuery, setDisplayedQuery] = useState<string>('');
    const [displayedCategory, setDisplayedCategory] = useState<string>('');

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
        });
        return () => unsubscribe();
    }, []);

    const incrementUsage = useCallback((type: 'search' | 'calc') => {
        if (currentUser) return; 
        if (type === 'search') {
            const next = searchCount + 1;
            setSearchCount(next);
            localStorage.setItem('search_usage', next.toString());
        } else {
            const next = calcCount + 1;
            setCalcCount(next);
            localStorage.setItem('calc_usage', next.toString());
        }
    }, [currentUser, searchCount, calcCount]);

    // Wrapped in useCallback to satisfy dependency rules
    const performSearch = useCallback(async (query: string, category: string | null, isExact: boolean) => {
        if (isSearchLocked) {
            navigate('/signin');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const response: SearchApiResponse = await searchTariffData(query, category, isExact);
            const formattedResults = response.results.map(formatTariffItemForDisplay);
            setFilteredResults(formattedResults);
            
            if (query || category) incrementUsage('search');

            setDisplayedQuery(response.query);
            setDisplayedCategory(response.category);
        } catch (err) {
            setError('Failed to fetch data. Please try again later.');
            setFilteredResults([]);
        } finally {
            setLoading(false);
        }
    }, [isSearchLocked, navigate, incrementUsage]);

    useEffect(() => {
        const loadCategories = async () => {
            try {
                const fetchedCategories = await getCategories();
                setCategories(fetchedCategories);
            } catch (err) { console.error(err); }
        };
        loadCategories();
        sendWakeUpRequest();
        const intervalId = setInterval(() => sendWakeUpRequest(), 14 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, []);

    // Fixed dependencies here
    useEffect(() => {
        if (debouncedSearchTerm !== displayedQuery || selectedCategory !== (displayedCategory || null)) {
            if (debouncedSearchTerm || selectedCategory) {
                performSearch(debouncedSearchTerm, selectedCategory, false);
            } else {
                setFilteredResults([]);
            }
        }
    }, [debouncedSearchTerm, selectedCategory, displayedQuery, displayedCategory, performSearch]);

    const handleSearchButtonClick = () => {
        if (isSearchLocked) { navigate('/signin'); return; }
        performSearch(searchTerm, selectedCategory, true);
    };

    const handleRowClick = async (hsCode: string) => {
        if (isCalcLocked) {
            alert("Calculation limit reached. Please sign in.");
            navigate('/signin');
            return;
        }
        try {
            const details = await getTaxDetails(hsCode);
            setSelectedTariffDetail(formatTariffItemForDisplay(details));
            incrementUsage('calc');
            setIsModalOpen(true);
        } catch (err) {
            console.error(err);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedTariffDetail(null);
    };

    return (
        <div className='home-page-wrapper'>
            {/* Background Image Container */}
            <div className="hero-background">
                {/* Image handled by CSS */}
                <div className="fallback-bg"></div>
                {/* Dark overlay for text visibility */}
                <div className="bg-overlay"></div>
            </div>

            <Navbar />
            
            {/* Hero Section */}
            <div className="hero-section">
                <div className="hero-content">
                    <h1 className="">
                        Sri Lanka Imports <span className="highlight-text">Tariff Search</span>
                    </h1>
                    <p className="hero-subtitle">
                        Instantly find HS Codes, calculate duties, and check exchange rates.
                    </p>

                    {/* Integrated Search Bar */}
                    <div className="hero-search-container">
                        <SearchBar
                            searchTerm={searchTerm}
                            onSearchTermChange={setSearchTerm}
                            onSearchSubmit={handleSearchButtonClick}
                            isLoading={loading}
                            isLocked={isSearchLocked}
                        />
                    </div>

                    {/* Usage Limits (Subtle) */}
                    {!currentUser && (
                        <div className="usage-limits-status">
                            <span className={`limit-badge ${searchCount >= SEARCH_LIMIT ? 'limit-reached' : ''}`}>
                                <FaSearch size={10} /> {Math.max(0, SEARCH_LIMIT - searchCount)} Searches Left
                            </span>
                            <span className="divider">•</span>
                            <span className={`limit-badge ${calcCount >= CALC_LIMIT ? 'limit-reached' : ''}`}>
                                <FaCalculator size={10} /> {Math.max(0, CALC_LIMIT - calcCount)} Calcs Left
                            </span>
                             <span className="divider">•</span>
                            <span className="login-hint" onClick={() => navigate('/signin')}>
                                <FaLock size={10} /> Sign in for unlimited
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="App content-container">
                {/* Feature Highlights (Only show when not searching) */}
                {!displayedQuery && !displayedCategory && (
                    <div className="features-grid">
                        <div className="feature-card" onClick={() => navigate('/exchange-rate')}>
                            <div className="feature-icon">💱</div>
                            <h3>Exchange Rates</h3>
                            <p>Live CBSL exchange rates for accurate duty calculation.</p>
                        </div>
                        <div className="feature-card" onClick={() => navigate('/agents')}>
                            <div className="feature-icon">🤝</div>
                            <h3>Clearing Agents</h3>
                            <p>Connect with verified customs clearing agents.</p>
                        </div>
                        <div className="feature-card" onClick={() => window.open('http://www.customs.gov.lk', '_blank')}>
                            <div className="feature-icon">⚖️</div>
                            <h3>Regulations</h3>
                            <p>Direct access to Sri Lanka Customs regulations.</p>
                        </div>
                    </div>
                )}

                {/* Search Results Area */}
                <div id="results-area">
                    {loading && (
                        <div className="loading-state">
                            <div className="loading-spinner-container">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                            </div>
                            <p className="loading-text">Searching tariff database...</p>
                            <p className="loading-subtext">Matching HS codes & descriptions</p>
                        </div>
                    )}
                    
                    {error && (
                        <div className="error-state">
                            <div className="error-icon">⚠️</div>
                            <h3>Something went wrong</h3>
                            <p>{error}</p>
                        </div>
                    )}
                    
                    {!loading && !error && filteredResults.length > 0 && (
                        <>
                            <div className="results-header">
                                <div className="results-header-left">
                                    <div className="results-count-badge">
                                        <span className="results-count-number">{filteredResults.length}</span>
                                        <span className="results-count-label">results</span>
                                    </div>
                                    <div className="results-header-info">
                                        <h2>Search Results</h2>
                                        <p>Showing tariffs for "<strong>{displayedQuery || displayedCategory}</strong>"</p>
                                    </div>
                                </div>
                                <button className="clear-results-btn" onClick={() => {
                                    setSearchTerm('');
                                    setFilteredResults([]);
                                    setDisplayedQuery('');
                                    setDisplayedCategory('');
                                }}>
                                    ✕ Clear
                                </button>
                            </div>
                            <TariffTable data={filteredResults} onRowClick={handleRowClick} />
                        </>
                    )}

                    {!loading && !error && filteredResults.length === 0 && (displayedQuery || displayedCategory) && (
                        <div className="empty-results-state">
                            <div className="empty-results-icon">
                                <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                                    <circle cx="35" cy="35" r="25" stroke="#cbd5e1" strokeWidth="4" fill="#f1f5f9"/>
                                    <line x1="53" y1="53" x2="70" y2="70" stroke="#cbd5e1" strokeWidth="4" strokeLinecap="round"/>
                                    <line x1="28" y1="35" x2="42" y2="35" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <h3>No tariffs found</h3>
                            <p>We couldn't find any results for "<strong>{displayedQuery || displayedCategory}</strong>"</p>
                            <p className="empty-results-hint">Try a different HS code, keyword, or broader search term</p>
                            <button className="clear-search-action-btn" onClick={() => {
                                setSearchTerm('');
                                setFilteredResults([]);
                                setDisplayedQuery('');
                                setDisplayedCategory('');
                            }}>Clear Search & Start Over</button>
                        </div>
                    )}
                </div>
                
                <TariffDetailModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    tariffItem={selectedTariffDetail}
                />
            </div>
            
            <Analytics />
            <Footer />

            <style>{`
                .home-page-wrapper {
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    background-color: transparent;
                }
                .hero-section {
                    color: white;
                    padding: 120px 20px 80px;
                    text-align: center;
                    position: relative;
                    overflow: hidden;
                }
                /* Removed duplicate hero-section::before background */
                .hero-content {
                    position: relative;
                    z-index: 2;
                    max-width: 900px;
                    margin: 0 auto;
                }
                
                .highlight-text {
                    color: #3b82f6;
                    background: linear-gradient(120deg, #3b82f6 0%, #60a5fa 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                .hero-subtitle {
                    font-size: 1.25rem;
                    color: #94a3b8;
                    margin-bottom: 3rem;
                    font-weight: 400;
                }
                .hero-search-container {
                    margin-bottom: 1.5rem;
                }
                .usage-limits-status {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    gap: 10px;
                    font-size: 0.9rem;
                    color: #94a3b8;
                    background: rgba(255,255,255,0.05);
                    padding: 8px 16px;
                    border-radius: 20px;
                    display: inline-flex;
                    border: 1px solid rgba(255,255,255,0.1);
                }
                .limit-reached { color: #f87171; }
                .login-hint { cursor: pointer; color: #60a5fa; font-weight: 600; display: flex; align-items: center; gap: 5px; }
                .login-hint:hover { text-decoration: underline; }
                
                .content-container {
                    margin-top: -40px; /* Overlap hero */
                    position: relative;
                    background: transparent;
                    box-shadow: none;
                    padding: 0 20px;
                }
                .features-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 20px;
                    max-width: 1200px;
                    margin: 0 auto 40px;
                }
                .feature-card {
                    background: white;
                    padding: 30px;
                    border-radius: 16px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
                    transition: transform 0.2s, box-shadow 0.2s;
                    cursor: pointer;
                    text-align: left;
                    border: 1px solid #e2e8f0;
                }
                .feature-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    border-color: #3b82f6;
                }
                .feature-icon { font-size: 2.5rem; margin-bottom: 15px; }
                .feature-card h3 { color: #1e293b; margin-bottom: 10px; font-size: 1.25rem; }
                .feature-card p { color: #64748b; font-size: 0.95rem; line-height: 1.5; }

                .results-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                    padding: 20px 24px;
                    background: white;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
                }
                .results-header-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                .results-count-badge {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    color: white;
                    border-radius: 14px;
                    padding: 10px 16px;
                    min-width: 56px;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
                }
                .results-count-number {
                    font-size: 1.5rem;
                    font-weight: 800;
                    line-height: 1;
                }
                .results-count-label {
                    font-size: 0.65rem;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    opacity: 0.85;
                }
                .results-header-info h2 {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0 0 4px;
                }
                .results-header-info p {
                    font-size: 0.88rem;
                    color: #64748b;
                    margin: 0;
                }
                .clear-results-btn {
                    background: #f1f5f9;
                    border: 1px solid #e2e8f0;
                    color: #64748b;
                    padding: 8px 16px;
                    border-radius: 10px;
                    font-weight: 600;
                    font-size: 0.85rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .clear-results-btn:hover {
                    background: #e2e8f0;
                    color: #334155;
                }

                /* Loading State */
                .loading-state {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    gap: 16px;
                }
                .loading-spinner-container {
                    position: relative;
                    width: 64px;
                    height: 64px;
                }
                .loading-ring {
                    position: absolute;
                    inset: 0;
                    border: 4px solid #e2e8f0;
                    border-top-color: #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                .loading-ring-inner {
                    position: absolute;
                    inset: 10px;
                    border: 3px solid #e2e8f0;
                    border-bottom-color: #60a5fa;
                    border-radius: 50%;
                    animation: spin 0.7s linear infinite reverse;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                .loading-text {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #1e293b;
                    margin: 0;
                }
                .loading-subtext {
                    font-size: 0.88rem;
                    color: #94a3b8;
                    margin: 0;
                }

                /* Error State */
                .error-state {
                    text-align: center;
                    padding: 40px 20px;
                    background: #fef2f2;
                    border: 1px solid #fecaca;
                    border-radius: 16px;
                }
                .error-icon { font-size: 2.5rem; margin-bottom: 12px; }
                .error-state h3 { color: #991b1b; margin: 0 0 8px; }
                .error-state p { color: #b91c1c; margin: 0; }

                /* Empty Results State */
                .empty-results-state {
                    text-align: center;
                    padding: 60px 20px;
                    background: white;
                    border: 1px dashed #cbd5e1;
                    border-radius: 20px;
                }
                .empty-results-icon {
                    margin-bottom: 20px;
                    opacity: 0.8;
                }
                .empty-results-state h3 {
                    font-size: 1.3rem;
                    font-weight: 700;
                    color: #334155;
                    margin: 0 0 8px;
                }
                .empty-results-state p {
                    color: #64748b;
                    margin: 0 0 6px;
                    font-size: 0.95rem;
                }
                .empty-results-hint {
                    font-size: 0.85rem !important;
                    color: #94a3b8 !important;
                    margin-bottom: 20px !important;
                }
                .clear-search-action-btn {
                    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
                    color: white;
                    border: none;
                    padding: 12px 28px;
                    border-radius: 12px;
                    font-weight: 600;
                    font-size: 0.9rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
                }
                .clear-search-action-btn:hover {
                    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                    box-shadow: 0 6px 16px rgba(37, 99, 235, 0.35);
                    transform: translateY(-1px);
                }

                /* Mobile Responsiveness */
                @media (max-width: 768px) {
                    .hero-section {
                        padding: 80px 20px 60px;
                    }
                    .hero-section h1 {
                        font-size: 2rem;
                    }
                    .hero-subtitle {
                        font-size: 1.1rem;
                        margin-bottom: 2rem;
                    }
                    .usage-limits-status {
                        flex-wrap: wrap;
                        gap: 8px;
                        padding: 10px 12px;
                        line-height: 1.4;
                    }
                    .usage-limits-status .divider {
                        display: none;
                    }
                    .content-container {
                        margin-top: -20px;
                        padding: 0 15px;
                    }
                    .features-grid {
                        grid-template-columns: 1fr;
                        gap: 15px;
                    }
                    .feature-card {
                        padding: 20px;
                    }
                    .results-header {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                        padding: 16px;
                    }
                    .results-header-left {
                        width: 100%;
                    }
                    .results-count-badge {
                        padding: 8px 12px;
                        min-width: 48px;
                    }
                    .results-count-number {
                        font-size: 1.2rem;
                    }
                    .results-header-info h2 {
                        font-size: 1.1rem;
                    }
                    .clear-results-btn {
                        align-self: flex-end;
                    }
                    .empty-results-state {
                        padding: 40px 20px;
                    }
                }

                @media (max-width: 480px) {
                    .hero-section h1 {
                        font-size: 1.75rem;
                    }
                    .hero-subtitle {
                        font-size: 1rem;
                    }
                }

                
            `}</style>
        </div>
    );
};

export default Home;